import type {CreateExtArgs, Extension, ExtensionAPI} from '@wix/document-manager-core'

export enum PayloadStatus {
    COMPLETED = 'COMPLETED',
    RUNNING = 'RUNNING',
    FAILED = 'FAILED'
}

export enum HandlerStatus {
    COMPLETED = 'COMPLETED',
    RUNNING = 'RUNNING',
    FAILED = 'FAILED'
}

export interface PayloadData {
    status: PayloadStatus
    output: any
}

export interface Payload {
    id: string
    data: PayloadData
}

export interface HandlerResult {
    status: HandlerStatus
    reason?: string
}

export type Handler = (data: any) => Promise<HandlerResult>

export const DEFAULT_HANDLER_TIMEOUT_MS = 60000

export interface EventHandlerApi {
    registerExecutionIdToHandler(executionId: string, handler: Handler, timeoutMs?: number): void
    invokeExecutionHandler(payload: Payload): Promise<void>
}

export type EventHandlerExtApi = ExtensionAPI & {eventHandler: EventHandlerApi}

const createExtension = (): Extension => {
    const executionIdToHandlerMap: Record<string, Handler> = {}

    const createExtensionAPI = ({coreConfig}: CreateExtArgs): EventHandlerExtApi => {
        const {logger} = coreConfig
        const removeExecutionIdToHandler = (executionId: string) => {
            delete executionIdToHandlerMap[executionId]
        }

        const getExecutionIdToHandler = (executionId: string) => {
            return executionIdToHandlerMap[executionId]
        }

        const invokeExecutionHandler = async (payload: Payload) => {
            const {id: executionId, data} = payload ?? {}

            const handler = getExecutionIdToHandler(executionId)

            logger.interactionStarted(`handler_execution`, {
                extras: {
                    executionId,
                    payload,
                    handlerExists: !!handler
                }
            })

            try {
                const result = await handler?.(data)
                const {reason} = result ?? {}

                if (result.status === 'FAILED') {
                    logger.captureError(new Error('handler failed'), {extras: {executionId, payload, reason}})
                }

                logger.interactionEnded(`handler_execution`, {
                    extras: {
                        handlerResult: result,
                        executionId
                    }
                })

                if (result.status !== 'RUNNING') {
                    removeExecutionIdToHandler(executionId)
                }
            } catch (error) {
                logger.captureError(new Error('handler failed'), {extras: {executionId, payload, reason: error}})

                logger.interactionEnded(`handler_execution`, {
                    extras: {
                        handlerResult: {status: 'FAILED', reason: error},
                        executionId
                    }
                })

                removeExecutionIdToHandler(executionId)
            }
        }

        const registerExecutionIdToHandler = (
            executionId: string,
            handler: Handler,
            timeoutMs: number = DEFAULT_HANDLER_TIMEOUT_MS
        ) => {
            logger.interactionStarted(`handler_registered`, {
                extras: {executionId}
            })
            executionIdToHandlerMap[executionId] = handler

            setTimeout(() => {
                if (!getExecutionIdToHandler(executionId)) return
                logger.captureError(new Error('handler timeout'), {extras: {executionId}})
                invokeExecutionHandler({
                    id: executionId,
                    data: {status: PayloadStatus.FAILED, output: {}}
                })
            }, timeoutMs)

            logger.interactionEnded(`handler_registered`, {
                extras: {executionId}
            })
        }

        return {
            eventHandler: {
                registerExecutionIdToHandler,
                invokeExecutionHandler
            }
        }
    }

    return {
        name: 'eventHandler',
        createExtensionAPI
    }
}

export {createExtension}
